import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { set_notification_variable } from '../../../redux/action_creators/home_actions';
import { get_api, put_api } from '../../../redux/api_funcs';
import { get_api_url } from '../../../utils/urls';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';
import { useResyncOnReconnect } from './resync_hooks';

const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return get_api_url(endpoint);
};

function AvailabilityToggle() {
  const dispatch = useDispatch();
  // The care_uuid cookie is a shared constant for the care chat user, not the
  // signed-in agent — availability is per-agent, so it must come from the auth
  // token.
  const currentUserUUID = getAuthUserInfoOrDefault().uuid || '';
  const [available, setAvailable] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchAvailability = useCallback(async () => {
    try {
      const resp = await get_api(getUrl('/v2/twilio_call/availability'), true);
      const next = !!resp.data?.data?.available;
      setAvailable(next);
      window.dispatchEvent(
        new CustomEvent('twilio-agent-availability-changed', {
          detail: { available: next, ops_uuid: currentUserUUID || '' }
        })
      );
    } catch (e) {
      dispatch(set_notification_variable(true, 'error', 'Failed to fetch agent availability'));
    }
  }, [dispatch, currentUserUUID]);

  // Fetch availability on mount and on every reconnect/tab-visible edge, then
  // rely on the `twilio-agent-availability-changed` window event for updates. Local
  // toggles dispatch it directly; server-side changes arrive via a chat
  // relay broadcast to all care users that home/index translates into the
  // same event. The broadcast carries the toggling user's ops_uuid so we
  // only update *this* user's button — every other agent's button would
  // otherwise flip whenever anyone toggled.
  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  useResyncOnReconnect(fetchAvailability);

  useEffect(() => {
    const onChange = (e) => {
      const detail = e?.detail || {};
      // Without a known current uuid we cannot tell whose toggle this is, so
      // drop the event rather than adopting another agent's status.
      if (!currentUserUUID || (detail.ops_uuid && detail.ops_uuid !== currentUserUUID)) {
        return;
      }
      setAvailable(!!detail.available);
    };
    window.addEventListener('twilio-agent-availability-changed', onChange);
    return () => window.removeEventListener('twilio-agent-availability-changed', onChange);
  }, [currentUserUUID]);

  const toggleAvailability = async () => {
    if (toggling) return;
    const next = !available;
    setToggling(true);
    setAvailable(next);
    window.dispatchEvent(
      new CustomEvent('twilio-agent-availability-changed', {
        detail: { available: next, ops_uuid: currentUserUUID || '' }
      })
    );
    try {
      const resp = await put_api(
        getUrl('/v2/twilio_call/availability'),
        { available: next, source: 'user' },
        true
      );
      // The server is the only authority on the stored flag; the optimistic
      // value is just a placeholder until it answers.
      const confirmed = !!resp?.data?.data?.available;
      setAvailable(confirmed);
      if (confirmed !== next) {
        window.dispatchEvent(
          new CustomEvent('twilio-agent-availability-changed', {
            detail: { available: confirmed, ops_uuid: currentUserUUID || '' }
          })
        );
      }
    } catch (e) {
      dispatch(set_notification_variable(true, 'error', 'Failed to update availability'));
      setAvailable(!next);
      window.dispatchEvent(
        new CustomEvent('twilio-agent-availability-changed', {
          detail: { available: !next, ops_uuid: currentUserUUID || '' }
        })
      );
      // The write may have landed even though the response did not, so ask the
      // server what it actually stored instead of trusting the reverted value.
      fetchAvailability();
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2.5">
      <div className="inline-flex flex-col items-end leading-tight whitespace-nowrap">
        <span className="text-[11px] font-medium text-white">Call status</span>
        <span className={`text-xs font-bold ${available ? 'text-green-500' : 'text-gray-500'}`}>
          {available ? 'Available' : 'Unavailable'}
        </span>
      </div>
      <button
        type="button"
        onClick={toggleAvailability}
        disabled={toggling}
        role="switch"
        aria-checked={available}
        aria-label="Toggle availability"
        className={`relative inline-flex h-7 w-[60px] shrink-0 items-center rounded-full p-[3px] border-0 transition-colors duration-300 ease-in-out focus:outline-none ${
          available ? 'bg-orange-500' : 'bg-gray-300'
        } ${toggling ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
      >
        <span
          aria-hidden="true"
          className={`absolute left-2 text-[10px] font-bold uppercase tracking-wider text-white transition-opacity duration-200 ${
            available ? 'opacity-100' : 'opacity-0'
          }`}
        >
          On
        </span>
        <span
          aria-hidden="true"
          className={`absolute right-2 text-[10px] font-bold uppercase tracking-wider text-gray-600 transition-opacity duration-200 ${
            available ? 'opacity-0' : 'opacity-100'
          }`}
        >
          Off
        </span>
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-[22px] w-[22px] rounded-full bg-white shadow-sm transform transition-transform duration-300 ease-in-out ${
            available ? 'translate-x-[32px]' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

export default AvailabilityToggle;
